import {
    Alert,
    Box,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { IconWrapper, Title } from '../../pages/auth/styled';
import Modal from 'react-responsive-modal';
import ButtonGeneral from '../general/buttons/buttonGeneral';
import Save from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

interface Props {
    file: File;
    open: boolean;
    onCloseCsvViewer: () => void;
    onAccept?: () => void;
    requiredFields?: string[];
}

interface PropsHead {
    headCells: { id: string; label: string }[];
    requiredFields?: string[];
}

interface PropsBody {
    data: any[];
    headCells: { id: string; label: string }[];
    page: number;
    rowsPerPage: number;
}

const Head = ({ headCells, requiredFields }: PropsHead) => {
    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell, i) => (
                    <TableCell
                        key={`HEAD-${headCell.id}-${i}`}
                        style={
                            requiredFields &&
                            !requiredFields.find(
                                (item) => item === headCell.label
                            )
                                ? { color: 'red' }
                                : {}
                        }
                    >
                        {headCell.label}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

const Body = ({ data, headCells, page, rowsPerPage }: PropsBody) => {
    return (
        <TableBody>
            {data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, i) => (
                    <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={`ITEM-${row.code}-${i}`}
                    >
                        {headCells.map((column, j) => (
                            <TableCell key={`CELL-${column.id}-${j}`}>
                                <Typography noWrap fontWeight="lg">
                                    {row[column.id]}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
        </TableBody>
    );
};

export const CSVRender = ({
    file,
    open,
    onCloseCsvViewer,
    onAccept,
    requiredFields,
}: Props) => {
    const [dataCsv, setDataCsv] = useState<any[]>([]);
    const [headCells, setHeadCells] = useState<{ id: string; label: string }[]>(
        []
    );
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const invalidFields = () => {
        const keys = dataCsv[0] ? Object.keys(dataCsv[0]) : [];
        return (
            requiredFields &&
            requiredFields.length > 0 &&
            keys.filter((k) => requiredFields.find((rf) => rf === k)).length !==
                requiredFields.length
        );
    };

    const withExtraData =
        requiredFields?.length !== Object.keys(dataCsv[0] || {}).length;

    useEffect(() => {
        (async () => {
            setLoading(true);
            setDataCsv([]);
            if (!file) return;
            const reader = new FileReader();
            const data: any[] = await new Promise((res) => {
                reader.onload = (event) => {
                    const workbook = XLSX.read(
                        event?.target?.result
                            ?.toString()
                            .replace('data:text/csv;base64,', ''),
                        { cellDates: true, raw: true }
                    );
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const data = XLSX.utils.sheet_to_json(worksheet, {
                        defval: '',
                    });
                    res(data);
                };

                reader.readAsDataURL(file);
            });
            setDataCsv(data);

            const keys = Object.keys(data[0] as any);
            setHeadCells(keys.map((k) => ({ id: k, label: k })));
            setLoading(false);
        })();
    }, [file]);

    return (
        <Modal
            open={open}
            onClose={onCloseCsvViewer}
            styles={{ modal: { maxWidth: '80%', minWidth: '80%' } }}
        >
            <Box sx={{ mt: 1 }}>
                <Title variant="h4">Se cargaran los siguientes datos</Title>
                {loading ? (
                    <IconWrapper>
                        <CircularProgress size={70} thickness={2} />
                    </IconWrapper>
                ) : (
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table stickyHeader aria-label="sticky table">
                                <Head
                                    headCells={headCells}
                                    requiredFields={requiredFields}
                                />
                                <Body
                                    data={dataCsv}
                                    headCells={headCells}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                />
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 100]}
                            component="div"
                            count={dataCsv.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                        {(invalidFields() || withExtraData) && (
                            <Alert severity="error">
                                Se encontro que la estructura del archivo
                                cargado tiene inconsistencias o datos extra (los
                                campos requeridos son{' '}
                                <b>{requiredFields?.join(', ')}</b>)
                            </Alert>
                        )}
                    </Paper>
                )}
                {onAccept && !invalidFields() && (
                    <Grid item xs={12} sx={{ textAlign: 'right' }} mt={3}>
                        <ButtonGeneral
                            onClick={onCloseCsvViewer}
                            startIcon={<CancelIcon color="secondary" />}
                            title="Cancelar"
                        />
                        <ButtonGeneral
                            onClick={onAccept}
                            startIcon={<Save color="secondary" />}
                            title="Aceptar"
                            sx={{ ml: 1 }}
                        />
                    </Grid>
                )}
            </Box>
        </Modal>
    );
};
